import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import {
  I18nShape,
  T,
  useStaticIntl,
} from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { SvgIcon, SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { NavVertical, Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'
import { Text } from '@tribeplatform/react-ui-kit/Text'

type SidebarItem = {
  title: string
  path: string
  leadingIcon: SvgIconName
}

const sidebarItemsFactory = ({ $t }: I18nShape): SidebarItem[] => [
  {
    title: $t({
      defaultMessage: 'Page customizer',
      id: 'Generics.PageCustomizer',
    }),
    path: adminRoutes.design.customizer.root,
    leadingIcon: 'customizer',
  },
  {
    title: $t({
      defaultMessage: 'Site appearance',
      id: 'Generics.SiteAppearance',
    }),
    path: adminRoutes.design.appearance.root,
    leadingIcon: 'appearance',
  },
]

export function DesignRoute() {
  return (
    <div className="flex h-full">
      <BackgroundProvider backgroundType="surface">
        <SidebarRoute />
      </BackgroundProvider>
      <div className="flex-1 overflow-hidden">
        <EmptyStateRoute />
      </div>
    </div>
  )
}

function SidebarRoute() {
  const sidebarItems = useStaticIntl(sidebarItemsFactory)
  const { Link } = useRouter()

  return (
    <Sidebar size="lg" className="h-full">
      <Sidebar.Content>
        <NavVertical>
          <NavVertical.Group>
            <SectionHeader
              title={
                <T id="Generics.DesignStudio" defaultMessage="Design Studio" />
              }
              description={
                <T
                  id="Generics.DesignStudio.Description"
                  defaultMessage="Customize your community's visual design and layouts"
                />
              }
              className="px-2 sm:ms-3"
            />
            <div className="pt-3 space-y-1 flex flex-col">
              {sidebarItems.map(item => {
                return (
                  <NavVertical.Item
                    key={item.path}
                    as={Link}
                    href={item.path}
                    leadingIcon={<SvgIcon name={item.leadingIcon} />}
                    trailingAddon={<SvgIcon name="chevron-right" />}
                  >
                    {item.title}
                  </NavVertical.Item>
                )
              })}
            </div>
          </NavVertical.Group>
        </NavVertical>
      </Sidebar.Content>
    </Sidebar>
  )
}

function EmptyStateRoute() {
  return (
    <div className="flex-1 flex-col items-center gap-4 pt-20 bg-surface h-full hidden md:flex">
      <SvgIcon name="appearance" size="4x" />
      <div className="flex flex-col items-center gap-2">
        <Text variant="headingMd">
          <T
            id="Admin.Design.EmptyStateTitle"
            defaultMessage="Select a design tool to get started"
          />
        </Text>
        <Text variant="bodySm">
          <T
            id="Admin.Design.EmptyStateDescription"
            defaultMessage="Choose a design tool from the sidebar to manage your site appearance."
          />
        </Text>
      </div>
    </div>
  )
}
